import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Settings,
  X,
  Plus,
  Trash2,
  Save,
  Loader2,
  Router as RouterIcon,
  Info,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
} from "lucide-react";
import { resolveRemoteUrl } from "./RealRoutersList";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const emptySeg = () => ({ public_ip: "", ip_pool: "", port_range: "" });

export const RemoteModemConfigModal = ({ onClose, onSuccess }) => {
  const [routers, setRouters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [segments, setSegments] = useState([emptySeg()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/jaringan/mikrotik/routers`, {
          withCredentials: true,
        });
        setRouters(res.data);
        if (res.data.length) setSelectedId(res.data[0].id);
      } catch (e) {
        setError("Gagal memuat daftar router");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const r = routers.find((x) => x.id === selectedId);
    const cfg = r?.remote_modem || {};
    setEnabled(cfg.enabled !== false);
    setSegments(cfg.segments && cfg.segments.length ? cfg.segments.map((s) => ({ ...s })) : [emptySeg()]);
    setSuccess("");
    setError("");
    // eslint-disable-next-line
  }, [selectedId]);

  const updateSeg = (i, key, val) =>
    setSegments((prev) => prev.map((s, idx) => (idx === i ? { ...s, [key]: val } : s)));
  const addSeg = () => setSegments((prev) => [...prev, emptySeg()]);
  const removeSeg = (i) => setSegments((prev) => prev.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    setError("");
    setSuccess("");
    if (!selectedId) {
      setError("Pilih router terlebih dahulu");
      return;
    }
    const clean = segments.filter(
      (s) => s.public_ip.trim() && s.ip_pool.trim() && s.port_range.trim()
    );
    setSaving(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/jaringan/mikrotik/routers/${selectedId}/remote-modem`,
        { enabled, segments: clean },
        { withCredentials: true }
      );
      setSuccess("Pengaturan Remote Modem berhasil disimpan");
      setRouters((prev) =>
        prev.map((r) => (r.id === selectedId ? { ...r, remote_modem: { enabled, segments: clean } } : r))
      );
      onSuccess?.();
    } catch (e) {
      setError(e.response?.data?.detail || "Gagal menyimpan pengaturan");
    } finally {
      setSaving(false);
    }
  };

  // Live preview using the first valid segment: IP 10.10.10.70 -> URL
  const previewSeg = segments.find((s) => s.public_ip.trim() && s.ip_pool.trim() && s.port_range.trim());
  let previewIp = "";
  let previewUrl = "";
  if (previewSeg) {
    const start = String(previewSeg.ip_pool).split("-")[0].trim();
    const parts = start.split(".");
    if (parts.length === 4) {
      const lastOctet = Math.min(70, parseInt(parts[3] || "2", 10) + 3);
      previewIp = `${parts[0]}.${parts[1]}.${parts[2]}.${lastOctet}`;
      previewUrl = resolveRemoteUrl(previewIp, { enabled: true, segments: [previewSeg] }) || "";
    }
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fadeIn"
      data-testid="remote-modem-modal"
    >
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-[#0e1628] border border-slate-800 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-800 sticky top-0 bg-[#0e1628] z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-violet-500/20 text-violet-400 flex items-center justify-center">
              <Settings className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-black text-white">Pengaturan Remote Modem</h2>
              <p className="text-[11px] text-slate-400">
                Port-forwarding per router untuk akses modem pelanggan dari IP publik
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="remote-modem-close"
            className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-slate-400 text-xs">
              <Loader2 className="w-4 h-4 animate-spin text-violet-400" />
              <span>Memuat router...</span>
            </div>
          ) : routers.length === 0 ? (
            <div className="p-6 rounded-xl bg-slate-900 border border-slate-800 border-dashed text-center">
              <RouterIcon className="w-8 h-8 mx-auto text-slate-600 mb-2" />
              <p className="text-sm font-bold text-slate-300">Belum ada router terdaftar</p>
              <p className="text-xs text-slate-500 mt-1">
                Tambahkan router MikroTik terlebih dahulu sebelum mengatur Remote Modem.
              </p>
            </div>
          ) : (
            <>
              {/* Router selector */}
              <div>
                <label className="text-[11px] text-slate-400 font-semibold block mb-1.5">
                  Pilih Router (setiap router punya pengaturan sendiri)
                </label>
                <select
                  value={selectedId}
                  onChange={(e) => setSelectedId(e.target.value)}
                  data-testid="remote-modem-router-select"
                  className="w-full px-3 py-2.5 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-violet-500"
                >
                  {routers.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.nama} — {r.host}:{r.port}
                    </option>
                  ))}
                </select>
              </div>

              {/* Enabled toggle */}
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  data-testid="remote-modem-enabled"
                  className="w-4 h-4 accent-violet-500"
                />
                <span className="text-xs text-slate-300 font-medium">
                  Aktifkan tombol Remote Modem untuk router ini
                </span>
              </label>

              {/* Info box */}
              <div className="p-3 rounded-xl bg-violet-500/10 border border-violet-500/30 text-violet-200 text-[11px] flex items-start gap-2">
                <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-violet-400" />
                <span>
                  Contoh: pelanggan dengan IP PPPoE <b>10.10.10.70</b> akan diarahkan ke{" "}
                  <b>121.101.134.129:8070</b>. Port dihitung dari selisih IP terhadap awal pool
                  ditambah port awal.
                </span>
              </div>

              {/* Segments */}
              <div className="space-y-3">
                {segments.map((seg, i) => (
                  <div
                    key={i}
                    className="p-3 rounded-xl bg-slate-900/70 border border-slate-800 space-y-2.5"
                    data-testid={`remote-modem-segment-${i}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-bold text-slate-300">
                        Segmen IP Pool #{i + 1}
                      </span>
                      {segments.length > 1 && (
                        <button
                          onClick={() => removeSeg(i)}
                          data-testid={`remote-modem-remove-segment-${i}`}
                          className="p-1 rounded-md bg-slate-800 hover:bg-rose-900/60 text-slate-400 hover:text-rose-300"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-500 font-semibold block mb-1">IP Publik</label>
                      <input
                        type="text"
                        value={seg.public_ip}
                        onChange={(e) => updateSeg(i, "public_ip", e.target.value)}
                        placeholder="121.101.134.129"
                        data-testid={`remote-modem-public-ip-${i}`}
                        className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-xs font-mono text-slate-200 placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-violet-500"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      <div>
                        <label className="text-[10px] text-slate-500 font-semibold block mb-1">
                          Range IP Pool
                        </label>
                        <input
                          type="text"
                          value={seg.ip_pool}
                          onChange={(e) => updateSeg(i, "ip_pool", e.target.value)}
                          placeholder="10.10.10.2-10.10.10.254"
                          data-testid={`remote-modem-ip-pool-${i}`}
                          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-xs font-mono text-slate-200 placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-violet-500"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-slate-500 font-semibold block mb-1">
                          Range Port
                        </label>
                        <input
                          type="text"
                          value={seg.port_range}
                          onChange={(e) => updateSeg(i, "port_range", e.target.value)}
                          placeholder="8002:8254"
                          data-testid={`remote-modem-port-range-${i}`}
                          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-xs font-mono text-slate-200 placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-violet-500"
                        />
                      </div>
                    </div>
                  </div>
                ))}

                <button
                  onClick={addSeg}
                  data-testid="remote-modem-add-segment"
                  className="w-full py-2 rounded-xl border border-dashed border-slate-700 hover:border-violet-500/60 text-slate-400 hover:text-violet-300 text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Tambah Segmen IP Pool
                </button>
              </div>

              {/* Preview */}
              {previewUrl && (
                <div className="p-3 rounded-xl bg-slate-900 border border-slate-800 text-[11px] text-slate-400 flex items-center gap-2 flex-wrap">
                  <ExternalLink className="w-3.5 h-3.5 text-emerald-400" />
                  <span>Pratinjau:</span>
                  <span className="font-mono text-slate-300">{previewIp}</span>
                  <span>→</span>
                  <span className="font-mono text-emerald-300">{previewUrl}</span>
                </div>
              )}

              {error && (
                <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-300 text-xs flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" /> {error}
                </div>
              )}
              {success && (
                <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs flex items-center gap-2" data-testid="remote-modem-success">
                  <CheckCircle2 className="w-4 h-4" /> {success}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!loading && routers.length > 0 && (
          <div className="flex items-center justify-end gap-2 p-5 border-t border-slate-800 sticky bottom-0 bg-[#0e1628]">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold"
            >
              Tutup
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              data-testid="remote-modem-save"
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white text-xs font-bold flex items-center gap-2 shadow-lg shadow-violet-600/30 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              <span>{saving ? "Menyimpan..." : "Simpan Pengaturan"}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
